import React, { useState, useEffect, useMemo } from 'react'
import { ArrowLeftIcon, CheckIcon, GlobeAltIcon, TrashIcon } from '@heroicons/react/outline'
import connexion from '../../connexion'

const LANGS = ['fr', 'en', 'de', 'nl', 'it', 'es', 'pt']

const DEFAULT_LANGUAGES = {
  fr: { title: '', body: '', filled: false },
  en: { title: '', body: '', filled: false },
  de: { title: '', body: '', filled: false },
  nl: { title: '', body: '', filled: false },
  it: { title: '', body: '', filled: false },
  es: { title: '', body: '', filled: false },
  pt: { title: '', body: '', filled: false }
}

// Convertit le tableau DB [{lang, title, content}] vers la map interne
function translationsArrayToMap(arr) {
  const map = JSON.parse(JSON.stringify(DEFAULT_LANGUAGES))
  if (!arr || !arr.length)
    return map
  for (let i = 0; i < arr.length; i++) {
    const t = arr[i]
    if (map[t.lang] !== undefined)
      map[t.lang] = {
        title: t.title || '',
        body: t.content || '',
        filled: !!(t.title || t.content)
      }
  }
  return map
}

// Convertit la map interne vers le tableau API [{lang, title, content}]
function translationsMapToArray(map) {
  const result = []
  for (let i = 0; i < LANGS.length; i++) {
    const lang = LANGS[i]
    const entry = map[lang]
    if (entry && entry.filled && entry.title.trim() && entry.body.trim())
      result.push({ lang, title: entry.title, content: entry.body })
  }
  return result
}


export default function TemplateForm({
  initialTemplate,
  onCancel,
  onSubmit,
  onDelete,
  lang_default,
  saving = false
}) {
  const [internalName, setInternalName] = useState('')
  const [activeLang, setActiveLang] = useState('fr')
  const [translations, setTranslations] = useState(JSON.parse(JSON.stringify(DEFAULT_LANGUAGES)))
  const [destination, setDestination] = useState('{"type":"home"}')
  const [pages, setPages] = useState([])
  const [categories, setCategories] = useState([])

  useEffect(() => {
    Promise.all([
      fetch(connexion.connect.url + 'webapp/pages/list').then(r => r.json()),
      fetch(connexion.connect.url + 'webapp/categories/list').then(r => r.json())
    ]).then(([pagesData, categoriesData]) => {
      if (pagesData && pagesData !== false) setPages(pagesData)
      if (categoriesData && categoriesData !== false) setCategories(categoriesData)
    })
  }, [])

  useEffect(() => {
    if (initialTemplate)
    {
      setInternalName(initialTemplate.name || '')
      setTranslations(translationsArrayToMap(initialTemplate.translations))
      setDestination(initialTemplate.redirect ? JSON.stringify(initialTemplate.redirect) : '{"type":"home"}')
    }
    else
    {
      setInternalName('')
      setTranslations(JSON.parse(JSON.stringify(DEFAULT_LANGUAGES)))
      setDestination('{"type":"home"}')
    }
  }, [initialTemplate])

  const destinations = useMemo(() => {
    const lang = lang_default || 'fr'
    const list = [
      { value: '{"type":"home"}', label: "Page d'accueil" },
      { value: '{"type":"animations"}', label: "Planning d'animations" },
      { value: '{"type":"membres"}', label: 'Page membres' },
    ]
    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i]
      let name = ''
      for (let j = 0; j < cat.translations.length; j++) {
        if (cat.translations[j].lang === lang) { name = cat.translations[j].name; break }
      }
      if (!name && cat.translations.length > 0) name = cat.translations[0].name
      if (!name) continue
      list.push({ value: JSON.stringify({ type: 'category', category: String(cat._id) }), label: 'Rubrique — ' + name })
    }
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i]
      if (page.category !== null) continue
      let name = ''
      for (let j = 0; j < page.translations.length; j++) {
        if (page.translations[j].lang === lang) { name = page.translations[j].name; break }
      }
      if (!name && page.translations.length > 0) name = page.translations[0].name
      if (!name) continue
      list.push({ value: JSON.stringify({ type: 'page', page: String(page._id) }), label: 'Page — ' + name })
    }
    return list
  }, [pages, categories, lang_default])

  const handleTextChange = (field, value) => {
    setTranslations(prev => {
      const current = { ...prev[activeLang] }
      current[field] = value
      current.filled = current.title.trim().length > 0 || current.body.trim().length > 0
      return {
        ...prev,
        [activeLang]: current
      }
    })
  }

  const isFormValid = () => {
    if (!internalName.trim())
      return false
    if (!translations.fr.title.trim() || !translations.fr.body.trim())
      return false
    if (translations.fr.title.length > 40 || translations.fr.body.length > 140)
      return false
    for (const key of Object.keys(translations))
    {
      if (translations[key].filled)
      {
        if (translations[key].title.length > 40 || translations[key].body.length > 140)
          return false
      }
    }
    return true
  }

  const handleSave = () => {
    if (!isFormValid())
      return
    onSubmit({
      name: internalName,
      redirect: JSON.parse(destination),
      translations: translationsMapToArray(translations)
    })
  }

  const valid = isFormValid()

  return (
    <div className="space-y-6 animate-fade-in" id="template-form-view">

      {/* En-tête formulaire */}
      <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
        <button
          id="tpl-form-back-btn"
          onClick={onCancel}
          className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-800"
          style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ margin: 0 }}>
            {initialTemplate ? "Modifier le modèle de message" : "Rédiger un nouveau modèle push"}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Colonne gauche */}
        <div className="lg:col-span-2 space-y-5">

          {/* Nom interne */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-2">
            <label className="text-sm font-bold text-slate-800 block" style={{ margin: 0 }}>
              Nom interne du template <span className="text-red-500">*</span>
            </label>
            <input
              id="tpl-internal-name-input"
              type="text"
              placeholder="ex: Rappel Règlement - Shorts de bain interdits"
              value={internalName}
              onChange={(e) => setInternalName(e.target.value)}
              className="browser-default w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-indigo-100 focus:outline-none focus:border-indigo-400 text-slate-800 font-semibold"
            />
            <div className="p-3 bg-slate-50 rounded-lg flex gap-2 items-start leading-snug text-sm text-slate-500">
              <span className="bg-indigo-100 text-indigo-700 px-1 rounded font-bold flex-shrink-0" style={{ fontSize: '10px' }}>
                Dashboard Info
              </span>
              Ce titre sert uniquement à trier vos campagnes push entre animateurs et n'est jamais poussé sur les smartphones des campeurs.
            </div>
          </div>

          {/* Traductions multi-langues */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-2">
              <div className="flex items-center gap-1">
                <GlobeAltIcon className="w-4 h-4 text-slate-400" />
                <h3 className="font-bold text-sm text-slate-800" style={{ margin: 0 }}>Rédiger les fiches linguistiques</h3>
              </div>
              <span className="uppercase font-mono bg-red-50 text-red-700 px-1 rounded font-extrabold select-none" style={{ fontSize: '10px' }}>
                Français requis
              </span>
            </div>

            {/* Onglets langues */}
            <div className="flex flex-wrap gap-1 bg-slate-50 p-1 rounded-xl border border-slate-200">
              {Object.keys(translations).map((lang) => {
                const isSelected = activeLang === lang
                const hasText = translations[lang].filled
                return (
                  <button
                    id={`tpl-lang-tab-${lang}`}
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`py-1 px-3 rounded-lg text-sm font-semibold flex items-center gap-1 transition-all ${
                      isSelected
                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                    style={{ backgroundImage: 'none', backgroundColor: isSelected ? '#ffffff' : 'transparent' }}
                  >
                    <span className="uppercase">{lang}</span>
                    <span className={`w-1 h-1 rounded-full ${hasText ? 'bg-green-500' : 'bg-transparent'}`} />
                  </button>
                )
              })}
            </div>

            {/* Titre */}
            <div className="space-y-1">
              <div className="flex justify-between items-baseline">
                <label className="text-sm font-bold text-slate-700" style={{ margin: 0 }}>
                  Titre [{activeLang}] <span className="text-red-500">*</span>
                </label>
                <span className={'text-sm font-mono ' + (translations[activeLang].title.length > 40 ? 'text-red-600 font-bold' : 'text-slate-400')}>
                  {translations[activeLang].title.length} / 40
                </span>
              </div>
              <input
                id="tpl-title-input"
                type="text"
                placeholder="ex: 🏊‍♂️ Rappel : Slip de bain obligatoire"
                value={translations[activeLang].title}
                onChange={(e) => handleTextChange('title', e.target.value)}
                className={
                  'browser-default w-full text-sm rounded-xl border px-3 py-2 focus:ring-2 focus:outline-none transition-all ' +
                  (translations[activeLang].title.length > 40
                    ? 'border-red-400 text-red-900 bg-red-50 focus:ring-red-100'
                    : 'border-slate-200 text-slate-800 focus:ring-indigo-100 focus:border-indigo-400')
                }
              />
            </div>

            {/* Corps */}
            <div className="space-y-1">
              <div className="flex justify-between items-baseline">
                <label className="text-sm font-bold text-slate-700" style={{ margin: 0 }}>
                  Corps [{activeLang}] <span className="text-red-500">*</span>
                </label>
                <span className={'text-sm font-mono ' + (translations[activeLang].body.length > 140 ? 'text-red-600 font-bold' : 'text-slate-400')}>
                  {translations[activeLang].body.length} / 140
                </span>
              </div>
              <textarea
                id="tpl-body-input"
                rows={3}
                placeholder="ex: Suite aux règles d'hygiène, les caleçons de bain sont interdits pour l'accès aux bassins."
                value={translations[activeLang].body}
                onChange={(e) => handleTextChange('body', e.target.value)}
                className={
                  'browser-default w-full text-sm rounded-xl border px-3 py-2 focus:ring-2 focus:outline-none resize-none leading-normal transition-all ' +
                  (translations[activeLang].body.length > 140
                    ? 'border-red-400 text-red-900 bg-red-50 focus:ring-red-100'
                    : 'border-slate-200 text-slate-800 focus:ring-indigo-100 focus:border-indigo-400')
                }
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              id="tpl-form-cancel-btn"
              type="button"
              onClick={onCancel}
              className="px-5 py-2 border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 font-semibold text-sm rounded-lg transition-all"
              style={{ backgroundImage: 'none', backgroundColor: 'transparent' }}
            >
              Annuler
            </button>
            {initialTemplate && onDelete && (
              <button
                type="button"
                id="tpl-form-delete-btn"
                onClick={onDelete}
                className="px-4 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 font-semibold text-sm rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
                style={{ backgroundImage: 'none', backgroundColor: '#fff1f2' }}
                title="Supprimer définitivement ce modèle"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Supprimer
              </button>
            )}
            <button
              id="tpl-form-save-btn"
              type="button"
              onClick={handleSave}
              disabled={!valid || saving}
              className={
                'flex-1 font-semibold text-sm px-5 py-2 rounded-lg transition-all flex items-center justify-center gap-1 shadow-md ' +
                (valid && !saving
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white border border-indigo-700'
                  : 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed')
              }
              style={{ backgroundImage: 'none', backgroundColor: valid && !saving ? '#4f46e5' : '#f1f5f9' }}
            >
              <CheckIcon className="w-4 h-4" />
              {saving ? 'Enregistrement...' : 'Sauvegarder le modèle'}
            </button>
          </div>
        </div>

        {/* Colonne droite */}
        <div className="space-y-5">

          {/* Destination */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-2">
            <label className="text-sm font-bold text-slate-700 block" style={{ margin: 0 }}>
              Destination par défaut
            </label>
            <p className="text-sm text-slate-400 leading-relaxed">
              Redirection mobile pré-configurée quand ce modèle est chargé :
            </p>
            <select
              id="tpl-destination-select"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="browser-default w-full text-sm rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 focus:outline-none font-semibold"
            >
              {destinations.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Consignes UX */}
          <div className="bg-slate-900 text-slate-100 rounded-xl p-5 border border-slate-800 space-y-3 shadow-md">
            <h4 className="font-semibold text-sm text-yellow-400 tracking-wide uppercase" style={{ margin: 0 }}>Consignes UX</h4>
            <ul className="text-sm text-slate-300 space-y-2 list-disc pl-4 leading-relaxed">
              <li>Misez sur des emojis accrocheurs en début de titre (ex: <span role="img" aria-label="nageur">🏊‍♂️</span>, <span role="img" aria-label="ballon de football">⚽</span>, <span role="img" aria-label="pizza">🍕</span>).</li>
              <li>Conservez un style poli, concis et rassurant.</li>
            </ul>
          </div>

        </div>

      </div>
    </div>
  )
}
